import React, { useState, useEffect } from 'react';
import { getCompanySetting, putCompanySetting } from '../../../api/company/company';
import { useAuth } from '../../../context/AuthContext';
import GradientButton from '../../buttons/GradientButton';
import BackToPaymentSettings from '../components/BackToPaymentSettings';
import './payment-subsettings/PaymentSubsettings.css';

const TipSettings = ({ onBackToPayments, embedded = false }) => {
  const { companyData } = useAuth();
  const [bootstrapping, setBootstrapping] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tipSettings, setTipSettings] = useState({
    enabled: false,
    defaultTipPercentages: [10, 15, 18, 20],
    allowCustomTip: true,
    minimumTipAmount: 1,
    maximumTipAmount: 1000,
    maximumTipPercentage: 50
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setBootstrapping(true);
        setError(null);

        if (!companyData?.id) {
          throw new Error('Company ID not found');
        }

        const data = await getCompanySetting({ companyId: companyData.id, key: 'general' });
        if (data.tips) {
            const defaultPercentages = [10, 15, 18, 20];
            const existingPercentages = data.tips.defaultTipPercentages || [];
            const mergedPercentages = [...defaultPercentages];
            existingPercentages.forEach((percentage, index) => {
              if (index < 4) {
                mergedPercentages[index] = percentage;
              }
            });

            setTipSettings((prev) => ({
              ...prev,
              ...data.tips,
              defaultTipPercentages: mergedPercentages
            }));
          }
      } catch (err) {
        console.error('Error fetching tip settings:', err);
        setError('Failed to load tip settings');
      } finally {
        setBootstrapping(false);
      }
    };

    fetchSettings();
  }, [companyData?.id]);

  useEffect(() => {
    if (!success) return undefined;
    const t = window.setTimeout(() => setSuccess(false), 5000);
    return () => window.clearTimeout(t);
  }, [success]);

  const handleTipsToggle = (event) => {
    setTipSettings((prev) => ({
      ...prev,
      enabled: event.target.checked
    }));
  };

  const handleCustomTipToggle = (event) => {
    setTipSettings((prev) => ({
      ...prev,
      allowCustomTip: event.target.checked
    }));
  };

  const handlePercentageChange = (index, value) => {
    const newPercentages = [...tipSettings.defaultTipPercentages];
    newPercentages[index] = Number(value);
    setTipSettings((prev) => ({
      ...prev,
      defaultTipPercentages: newPercentages
    }));
  };

  const handleNumericChange = (field, value) => {
    setTipSettings((prev) => ({
      ...prev,
      [field]: Number(value)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      if (!companyData?.id) {
        throw new Error('Company ID not found');
      }

      const defaultPercentages = [10, 15, 18, 20];
      const currentPercentages = [...tipSettings.defaultTipPercentages];
      const finalPercentages = [...defaultPercentages];
      currentPercentages.forEach((percentage, index) => {
        if (index < 4) {
          finalPercentages[index] = percentage;
        }
      });

      const settingsToSave = {
        ...tipSettings,
        defaultTipPercentages: finalPercentages
      };

      await putCompanySetting({
        companyId: companyData.id,
        key: 'general',
        settings: { tips: settingsToSave },
      });

      setSuccess(true);
    } catch (err) {
      console.error('Error saving tip settings:', err);
      setError('Failed to save tip settings');
    } finally {
      setSaving(false);
    }
  };

  if (bootstrapping) {
    return (
      <div className={`ps-root ${embedded ? 'ps-root--embedded' : ''}`}>
        <div className="ps-loading">
          <div className="ps-skeleton" aria-hidden />
          <p className="ps-muted" style={{ marginTop: '1rem' }}>Loading tip settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`ps-root ${embedded ? 'ps-root--embedded' : ''}`}>
      {onBackToPayments && !embedded && <BackToPaymentSettings onBack={onBackToPayments} />}

      <label className="ps-option-box" htmlFor="tips-enabled">
        <input
          id="tips-enabled"
          type="checkbox"
          className="ps-checkbox"
          checked={tipSettings.enabled}
          onChange={handleTipsToggle}
        />
        <span className="ps-option-box__body">
          <span className="ps-option-box__title">Enable tipping for appointments</span>
          <p className="ps-option-box__hint">
            Tick the box to show tip options at checkout when your payment gateway supports tipping.
          </p>
        </span>
      </label>

      {tipSettings.enabled && (
        <>
          <hr className="ps-rule" />

          <h4 className="ps-section-head">Preset tip buttons</h4>
          <p className="ps-field-hint" style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}>
            Up to four suggested percentages clients can tap in one step.
          </p>
          <div className="ps-tip-presets">
            {tipSettings.defaultTipPercentages.map((percentage, index) => (
              <div className="ps-tip-preset" key={index}>
                <label className="ps-label" htmlFor={`tip-preset-${index}`}>
                  Option {index + 1}
                </label>
                <div className="ps-input-wrap">
                  <input
                    id={`tip-preset-${index}`}
                    type="number"
                    className="ps-input"
                    min={0}
                    max={100}
                    step={1}
                    value={percentage}
                    onChange={(e) => handlePercentageChange(index, e.target.value)}
                  />
                  <span className="ps-input-suffix">%</span>
                </div>
              </div>
            ))}
          </div>

          <label className="ps-option-box" htmlFor="tips-custom">
            <input
              id="tips-custom"
              type="checkbox"
              className="ps-checkbox"
              checked={tipSettings.allowCustomTip}
              onChange={handleCustomTipToggle}
            />
            <span className="ps-option-box__body">
              <span className="ps-option-box__title">Allow custom tip amounts</span>
              <p className="ps-option-box__hint">
                Tick the box to let clients enter their own tip instead of only presets.
              </p>
            </span>
          </label>

          <h4 className="ps-section-head">Limits</h4>
          <div className="ps-form-grid ps-form-grid--tight">
            <div className="ps-field">
              <label htmlFor="tip-min">Minimum tip</label>
              <div className="ps-input-wrap ps-input-wrap--prefix">
                <span className="ps-input-prefix">$</span>
                <input
                  id="tip-min"
                  type="number"
                  className="ps-input"
                  min={0}
                  step={0.01}
                  value={tipSettings.minimumTipAmount}
                  onChange={(e) => handleNumericChange('minimumTipAmount', e.target.value)}
                />
              </div>
            </div>
            <div className="ps-field">
              <label htmlFor="tip-max">Maximum tip</label>
              <div className="ps-input-wrap ps-input-wrap--prefix">
                <span className="ps-input-prefix">$</span>
                <input
                  id="tip-max"
                  type="number"
                  className="ps-input"
                  min={0}
                  step={0.01}
                  value={tipSettings.maximumTipAmount}
                  onChange={(e) => handleNumericChange('maximumTipAmount', e.target.value)}
                />
              </div>
            </div>
            <div className="ps-field">
              <label htmlFor="tip-max-pct">Max tip %</label>
              <div className="ps-input-wrap">
                <input
                  id="tip-max-pct"
                  type="number"
                  className="ps-input"
                  min={0}
                  max={100}
                  step={1}
                  value={tipSettings.maximumTipPercentage}
                  onChange={(e) => handleNumericChange('maximumTipPercentage', e.target.value)}
                />
                <span className="ps-input-suffix">%</span>
              </div>
            </div>
          </div>
        </>
      )}

      {error && (
        <div className="ps-alert ps-alert--error" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="ps-alert ps-alert--success" role="status">
          Settings saved successfully.
        </div>
      )}

      <div className="ps-actions ps-actions--flush" style={{ marginTop: '1rem' }}>
        <GradientButton variant="primary" className="ps-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <span className="ps-btn-spinner" />
              Saving…
            </>
          ) : (
            'Save changes'
          )}
        </GradientButton>
      </div>
    </div>
  );
};

export default TipSettings;
